import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ComponentWithBackgroundMap } from './components/component.model';
import { OpenPageModel, OpenPageService } from './components/menu-pages/services/open-page.service';
import { DialogComponent } from './shared/components/dialog/dialog.component';
import { GoogleAnalyticsService } from './shared/services/google-analytics/google-analytics.service';
import { PageHistoryService } from './shared/services/page-history.service';
import { SessionService } from './shared/services/session.service';
import { SidenavService } from './shared/services/sidenav.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  @ViewChild('sidenav', { static: true })
  public sidenav: MatSidenav;

  public opened: boolean;
  public showBackgroundMap: boolean;
  public openPage: OpenPageModel;
  public sessionExistence = false;
  public isChargingOverlayCollapsed$: Observable<boolean>;
  private isHome: boolean;

  constructor(
    public readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly pageHistoryService: PageHistoryService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly openPageService: OpenPageService,
    private readonly sidenavService: SidenavService,
    private readonly sessionService: SessionService,
    private readonly googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.sessionExistence = this.sessionService.getSessionExistence();

    this.initLanguagesAndLocale();

    this.route.queryParams.subscribe((params: Params) => {
      if (params['translate']) {
        if (
          environment.mainUrl.includes('dev') ||
          environment.mainUrl.includes('int') ||
          environment.mainUrl.includes('qa')
        ) {
          translate.setTranslation('en', {});
          translate.setTranslation('de', {});
        }
      }
      if (params['paypal'] === 'error') {
        this.sessionService.clearSession();
        this.matDialog.open(DialogComponent, {
          data: {
            headline: 'payment.error.headline',
            text: 'payment.error.paypal',
            button2: 'payment.error.ok',
          },
        });
      } else if (params['paypal'] === 'back') {
        this.sessionService.clearSession();
        this.matDialog.open(DialogComponent, {
          data: {
            headline: 'payment.abort.headline',
            button2: 'payment.abort.ok',
          },
        });
      } else if (params['cc'] === 'error') {
        this.sessionService.clearSession();
        this.matDialog.open(DialogComponent, {
          data: {
            headline: 'payment.error.headline',
            text: 'payment.error.cc',
            button2: 'payment.error.ok',
          },
        });
      } else if (params['cc'] === 'back') {
        this.sessionService.clearSession();
        this.matDialog.open(DialogComponent, {
          data: {
            headline: 'payment.abort.headline',
            button2: 'payment.abort.ok',
          },
        });
      }
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.isHome = '/' === new URL(location.href).pathname;
    });
  }

  public ngOnInit() {
    this.googleAnalyticsService.listenToRouteChanges();
    this.sidenavService.setSidenav(this.sidenav);
    this.openPageService.currentOpenPage.subscribe(openPage => {
      this.openPage = openPage;
    });
    this.sessionService.getSessionExistenceSubscription().subscribe(existence => {
      this.sessionExistence = existence;
    });
    this.isChargingOverlayCollapsed$ = this.sessionService.isOverlayCollapsed$;
  }

  public setActiveComponent(componentRef: any | ComponentWithBackgroundMap) {
    if (componentRef.openPage) {
      componentRef.openPage.subscribe(event => {
        this.onOpenPage(event);
      });
    }
    this.showBackgroundMap =
      typeof (componentRef as ComponentWithBackgroundMap).showBackground === 'function' &&
      componentRef.showBackground();
  }

  public navigateBack() {
    const url = this.pageHistoryService.pageHistoryBack();
    this.router.navigate([url]);
  }

  public closeMenuPages(sidenav: MatSidenav): void {
    sidenav.close();
  }

  public onSidenavClosed(): void {
    this.openPageService.closeOpenPage();
  }

  public onOpenPage(event) {
    switch (event) {
      case 'contactPage':
        this.sidenav.open();
        this.openPage.contactPageOpen = true;
        this.openPage.selectedPageOpen = true;
        this.openPageService.changeOpenPage(this.openPage);
        break;

      case 'terms':
        this.sidenav.open();
        this.openPage.termsOpen = true;
        this.openPage.selectedPageOpen = true;
        this.openPageService.changeOpenPage(this.openPage);
        break;

      case 'sfChatPage':
        this.sidenav.open();
        this.openPage.sfChatPageOpen = true;
        this.openPage.selectedPageOpen = true;
        this.openPageService.changeOpenPage(this.openPage);
        break;
    }
  }

  public backButtonVisible() {
    return (!this.sessionExistence && !this.isHome) || this.opened;
  }

  private initLanguagesAndLocale() {
    const registeredLanguages = ['de', 'en', 'fr'];
    const browserLanguage = this.translate.getBrowserLang();
    const selectedLang = registeredLanguages.includes(browserLanguage) ? browserLanguage : 'en';

    this.translate.use(selectedLang);
    this.translate.setFallbackLang('en');
    moment.locale(selectedLang);
  }
}
